import { utils } from "@intrasync/utils";
import type ServiceStore from "stores/ServiceStore";
import type RootStore from "stores/RootStore";
import { makeAutoObservable, when, runInAction } from "mobx";

// Enhanced theme system types
export type ThemeMode = "light" | "dark" | "auto" | "high-contrast";
export type ColorScheme =
  | "blue"
  | "green"
  | "purple"
  | "orange"
  | "red"
  | "custom";
export type FontSize = "small" | "medium" | "large" | "extra-large";
export type UIAnimation = "none" | "reduced" | "standard" | "enhanced";

export interface ThemeSettings {
  mode: ThemeMode;
  colorScheme: ColorScheme;
  fontSize: FontSize;
  animation: UIAnimation;
  customColors?: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

export interface UIState {
  // Loading states
  globalLoading: boolean;
  loadingOperations: Set<string>;

  // Dialog and modal states
  openDialogs: Set<string>;
  modalStack: string[];

  // Navigation states
  sidebarCollapsed: boolean;
  breadcrumbs: Array<{ label: string; path?: string }>;

  // Notification states
  notifications: Array<{
    id: string;
    type: "success" | "error" | "warning" | "info";
    message: string;
    timestamp: number;
    persistent?: boolean;
  }>;

  // Focus and keyboard navigation
  focusedElement: string | null;
  keyboardNavigationEnabled: boolean;

  // Search and filters
  globalSearchOpen: boolean;
  activeFilters: Record<string, any>;
}

export interface AccessibilitySettings {
  screenReaderEnabled: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
  keyboardNavigation: boolean;
  fontSize: FontSize;
  focusIndicators: boolean;
  skipLinks: boolean;
}

export interface LocalizationSettings {
  language: string;
  locale: string;
  dateFormat: string;
  timeFormat: "12h" | "24h";
  numberFormat: string;
  currency: string;
  timezone: string;
}

export interface PerformanceMetrics {
  renderTime: number;
  interactionLatency: number;
  memoryUsage: number;
  lastUpdated: number;
}

export interface UserInteraction {
  timestamp: number;
  action: string;
  target: string;
  metadata?: Record<string, any>;
}

class UIStore {
  rootStore: RootStore;

  // Enhanced theme system
  themeSettings: ThemeSettings = {
    mode: "auto",
    colorScheme: "blue",
    fontSize: "medium",
    animation: "standard",
  };

  // Legacy support
  darkMode: boolean = false;

  // Comprehensive UI state management
  uiState: UIState = {
    globalLoading: false,
    loadingOperations: new Set(),
    openDialogs: new Set(),
    modalStack: [],
    sidebarCollapsed: false,
    breadcrumbs: [],
    notifications: [],
    focusedElement: null,
    keyboardNavigationEnabled: false,
    globalSearchOpen: false,
    activeFilters: {},
  };

  // Accessibility settings
  accessibilitySettings: AccessibilitySettings = {
    screenReaderEnabled: false,
    highContrast: false,
    reducedMotion: false,
    keyboardNavigation: true,
    fontSize: "medium",
    focusIndicators: true,
    skipLinks: true,
  };

  // Localization settings
  localizationSettings: LocalizationSettings = {
    language: "en",
    locale: "en-US",
    dateFormat: "MM/dd/yyyy",
    timeFormat: "12h",
    numberFormat: "1,234.56",
    currency: "USD",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // Performance monitoring
  performanceMetrics: PerformanceMetrics = {
    renderTime: 0,
    interactionLatency: 0,
    memoryUsage: 0,
    lastUpdated: Date.now(),
  };

  // User interaction tracking
  interactions: UserInteraction[] = [];
  private interactionBuffer: UserInteraction[] = [];
  private maxInteractions = 1000;

  // Worker management
  worker: Worker | SharedWorker | null = null;
  workerState: "idle" | "connecting" | "connected" | "error" = "idle";
  workerErrorCount = 0;
  private maxWorkerRetries = 3;
  private workerRetryTimeout: ReturnType<typeof setTimeout> | null = null;

  // Performance optimization
  private performanceObserver: PerformanceObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });

    this.rootStore = rootStore;

    // Initialize enhanced UI systems
    this.initializeThemeSystem();
    this.initializeAccessibilitySettings();
    this.initializeLocalization();
    this.initializePerformanceMonitoring();
    this.initializeKeyboardNavigation();

    // Load user preferences
    this.loadUserPreferences();

    // Set up system preference listeners
    this.setupSystemPreferenceListeners();

    // Initialize worker when authenticated
    when(
      () => !!this.rootStore.authStore?.isSignedIn,
      () => {
        this.findJobs();
        // this.spinUpWorker();
      }
    );
  }

  // ========================================
  // INITIALIZATION METHODS
  // ========================================

  private initializeThemeSystem = () => {
    // Detect system theme preference
    if (this.themeSettings.mode === "auto") {
      this.updateThemeFromSystem();
    }

    // Apply initial theme
    this.applyTheme();
  };

  private initializeAccessibilitySettings = () => {
    // Detect system accessibility preferences
    if (typeof window !== "undefined") {
      try {
        this.accessibilitySettings.reducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        this.accessibilitySettings.highContrast = window.matchMedia(
          "(prefers-contrast: high)"
        ).matches;

        // Detect screen reader usage
        this.detectScreenReader();
      } catch (err) {
        console.warn("Could not detect accessibility preferences:", err);
      }
    }
  };

  private initializeLocalization = () => {
    // Auto-detect user's locale if not set
    if (typeof navigator !== "undefined") {
      this.localizationSettings.language = navigator.language.split("-")[0];
      this.localizationSettings.locale = navigator.language;
    }
  };

  private initializePerformanceMonitoring = () => {
    if (typeof window !== "undefined" && "PerformanceObserver" in window) {
      try {
        this.performanceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          this.processPerformanceEntries(entries);
        });

        this.performanceObserver.observe({
          entryTypes: ["measure", "navigation", "paint"],
        });
      } catch (err) {
        console.warn("Performance monitoring not available:", err);
      }
    }
  };

  private initializeKeyboardNavigation = () => {
    if (typeof window !== "undefined") {
      // Detect if user is using keyboard navigation
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Tab") {
          this.uiState.keyboardNavigationEnabled = true;
          this.accessibilitySettings.keyboardNavigation = true;
        }
      };

      const handleMouseDown = () => {
        this.uiState.keyboardNavigationEnabled = false;
      };

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("mousedown", handleMouseDown);
    }
  };

  private loadUserPreferences = () => {
    try {
      const savedPreferences = localStorage.getItem("ui-preferences");
      if (savedPreferences) {
        const preferences = JSON.parse(savedPreferences);
        this.themeSettings = { ...this.themeSettings, ...preferences.theme };
        this.accessibilitySettings = {
          ...this.accessibilitySettings,
          ...preferences.accessibility,
        };
        this.localizationSettings = {
          ...this.localizationSettings,
          ...preferences.localization,
        };
      }
    } catch (err) {
      console.warn("Could not load user preferences:", err);
    }
  };

  private saveUserPreferences = () => {
    try {
      const preferences = {
        theme: this.themeSettings,
        accessibility: this.accessibilitySettings,
        localization: this.localizationSettings,
      };
      localStorage.setItem("ui-preferences", JSON.stringify(preferences));
    } catch (err) {
      console.warn("Could not save user preferences:", err);
    }
  };

  private setupSystemPreferenceListeners = () => {
    if (typeof window !== "undefined") {
      try {
        // Theme preference listener
        const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
        darkModeQuery.addEventListener("change", this.handleSystemThemeChange);

        // Accessibility preference listeners
        const reducedMotionQuery = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        );
        reducedMotionQuery.addEventListener(
          "change",
          this.handleReducedMotionChange
        );

        const highContrastQuery = window.matchMedia("(prefers-contrast: high)");
        highContrastQuery.addEventListener(
          "change",
          this.handleHighContrastChange
        );
      } catch (err) {
        console.warn("Could not setup system preference listeners:", err);
      }
    }
  };

  private detectScreenReader = () => {
    // Multiple methods to detect screen reader usage
    if (typeof window !== "undefined") {
      // Method 1: Check for screen reader specific properties
      const hasScreenReaderProperties = !!(
        (window as any).speechSynthesis ||
        (window as any).webkitSpeechSynthesis ||
        (navigator as any).userAgent.includes("NVDA") ||
        (navigator as any).userAgent.includes("JAWS")
      );

      // Method 2: Check for high contrast mode (often used with screen readers)
      const hasHighContrast = window.matchMedia(
        "(prefers-contrast: high)"
      ).matches;

      this.accessibilitySettings.screenReaderEnabled =
        hasScreenReaderProperties || hasHighContrast;
    }
  };

  // ========================================
  // ENHANCED THEME SYSTEM
  // ========================================

  setThemeMode = (mode: ThemeMode) => {
    this.themeSettings.mode = mode;
    this.updateThemeFromSystem();
    this.applyTheme();
    this.saveUserPreferences();
  };

  setColorScheme = (scheme: ColorScheme) => {
    this.themeSettings.colorScheme = scheme;
    this.applyTheme();
    this.saveUserPreferences();
  };

  setFontSize = (size: FontSize) => {
    this.themeSettings.fontSize = size;
    this.accessibilitySettings.fontSize = size;
    this.applyTheme();
    this.saveUserPreferences();
  };

  setAnimation = (animation: UIAnimation) => {
    this.themeSettings.animation = animation;
    this.applyTheme();
    this.saveUserPreferences();
  };

  setCustomColors = (colors: {
    primary: string;
    secondary: string;
    accent: string;
  }) => {
    this.themeSettings.customColors = colors;
    if (this.themeSettings.colorScheme !== "custom") {
      this.themeSettings.colorScheme = "custom";
    }
    this.applyTheme();
    this.saveUserPreferences();
  };

  private updateThemeFromSystem = () => {
    if (this.themeSettings.mode === "auto" && typeof window !== "undefined") {
      this.darkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    } else {
      this.darkMode =
        this.themeSettings.mode === "dark" ||
        this.themeSettings.mode === "high-contrast";
    }
  };

  private applyTheme = () => {
    if (typeof document !== "undefined") {
      const root = document.documentElement;

      // Apply theme mode
      root.setAttribute("data-theme-mode", this.themeSettings.mode);
      root.setAttribute("data-color-scheme", this.themeSettings.colorScheme);
      root.setAttribute("data-font-size", this.themeSettings.fontSize);
      root.setAttribute("data-animation", this.themeSettings.animation);

      // Apply custom colors if available
      if (this.themeSettings.customColors) {
        root.style.setProperty(
          "--color-primary",
          this.themeSettings.customColors.primary
        );
        root.style.setProperty(
          "--color-secondary",
          this.themeSettings.customColors.secondary
        );
        root.style.setProperty(
          "--color-accent",
          this.themeSettings.customColors.accent
        );
      }
    }
  };

  private handleSystemThemeChange = (e: MediaQueryListEvent) => {
    if (this.themeSettings.mode === "auto") {
      runInAction(() => {
        this.darkMode = e.matches;
        this.applyTheme();
      });
    }
  };

  private handleReducedMotionChange = (e: MediaQueryListEvent) => {
    runInAction(() => {
      this.accessibilitySettings.reducedMotion = e.matches;
      if (e.matches) {
        this.themeSettings.animation = "reduced";
        this.applyTheme();
      }
    });
  };

  private handleHighContrastChange = (e: MediaQueryListEvent) => {
    runInAction(() => {
      this.accessibilitySettings.highContrast = e.matches;
      if (e.matches && this.themeSettings.mode !== "high-contrast") {
        this.setThemeMode("high-contrast");
      }
    });
  };

  findJobs = () => {
    // Calling Find Jobs After Auth
    const jobStore = this.rootStore?.jobStore as ServiceStore;
    jobStore.find().catch((error) => {
      console.error("Find Jobs error:", error);
    });
  };

  // ========================================
  // UI STATE MANAGEMENT
  // ========================================

  // Loading state management
  startLoading = (operationId: string) => {
    this.uiState.loadingOperations.add(operationId);
    this.uiState.globalLoading = this.uiState.loadingOperations.size > 0;
  };

  stopLoading = (operationId: string) => {
    this.uiState.loadingOperations.delete(operationId);
    this.uiState.globalLoading = this.uiState.loadingOperations.size > 0;
  };

  clearAllLoading = () => {
    this.uiState.loadingOperations.clear();
    this.uiState.globalLoading = false;
  };

  // // Dialog and modal management
  // openDialog = (dialogId: string) => {
  //   this.uiState.openDialogs.add(dialogId);
  //   this.uiState.modalStack.push(dialogId);
  // };

  // closeDialog = (dialogId: string) => {
  //   this.uiState.openDialogs.delete(dialogId);
  //   const index = this.uiState.modalStack.indexOf(dialogId);
  //   if (index > -1) {
  //     this.uiState.modalStack.splice(index, 1);
  //   }
  // };

  // closeTopDialog = () => {
  //   const topDialog = this.uiState.modalStack.pop();
  //   if (topDialog) {
  //     this.uiState.openDialogs.delete(topDialog);
  //   }
  //   return topDialog;
  // };

  // closeAllDialogs = () => {
  //   this.uiState.openDialogs.clear();
  //   this.uiState.modalStack = [];
  // };

  // Navigation state management
  setSidebarCollapsed = (collapsed: boolean) => {
    this.uiState.sidebarCollapsed = collapsed;
  };

  setBreadcrumbs = (breadcrumbs: Array<{ label: string; path?: string }>) => {
    this.uiState.breadcrumbs = breadcrumbs;
  };

  addBreadcrumb = (breadcrumb: { label: string; path?: string }) => {
    this.uiState.breadcrumbs.push(breadcrumb);
  };

  // Notification management
  addNotification = (
    type: "success" | "error" | "warning" | "info",
    message: string,
    persistent = false
  ) => {
    const notification = {
      id: `notification-${Date.now()}-${Math.random()}`,
      type,
      message,
      timestamp: Date.now(),
      persistent,
    };

    this.uiState.notifications.push(notification);

    // Auto-remove non-persistent notifications
    if (!persistent) {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, 5000);
    }

    return notification.id;
  };

  removeNotification = (id: string) => {
    const index = this.uiState.notifications.findIndex((n) => n.id === id);
    if (index > -1) {
      this.uiState.notifications.splice(index, 1);
    }
  };

  clearAllNotifications = () => {
    this.uiState.notifications = [];
  };

  // Focus management
  setFocusedElement = (elementId: string | null) => {
    this.uiState.focusedElement = elementId;
  };

  // Search state management
  setGlobalSearchOpen = (open: boolean) => {
    this.uiState.globalSearchOpen = open;
  };

  setActiveFilters = (filters: Record<string, any>) => {
    this.uiState.activeFilters = filters;
  };

  updateFilter = (key: string, value: any) => {
    if (value === null || value === undefined || value === "") {
      delete this.uiState.activeFilters[key];
    } else {
      this.uiState.activeFilters[key] = value;
    }
  };

  clearFilters = () => {
    this.uiState.activeFilters = {};
  };

  // ========================================
  // ACCESSIBILITY MANAGEMENT
  // ========================================

  updateAccessibilitySetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    this.accessibilitySettings[key] = value;
    this.saveUserPreferences();

    // Apply immediate effects
    if (key === "fontSize") {
      this.setFontSize(value as FontSize);
    } else if (key === "reducedMotion") {
      this.setAnimation(value ? "reduced" : "standard");
    } else if (key === "highContrast") {
      this.setThemeMode(value ? "high-contrast" : "auto");
    }
  };

  // ========================================
  // LOCALIZATION MANAGEMENT
  // ========================================

  setLanguage = (language: string) => {
    this.localizationSettings.language = language;
    this.saveUserPreferences();
  };

  setLocale = (locale: string) => {
    this.localizationSettings.locale = locale;
    this.saveUserPreferences();
  };

  setDateFormat = (format: string) => {
    this.localizationSettings.dateFormat = format;
    this.saveUserPreferences();
  };

  setTimeFormat = (format: "12h" | "24h") => {
    this.localizationSettings.timeFormat = format;
    this.saveUserPreferences();
  };

  setCurrency = (currency: string) => {
    this.localizationSettings.currency = currency;
    this.saveUserPreferences();
  };

  setTimezone = (timezone: string) => {
    this.localizationSettings.timezone = timezone;
    this.saveUserPreferences();
  };

  // ========================================
  // PERFORMANCE MONITORING
  // ========================================

  private processPerformanceEntries = (entries: PerformanceEntry[]) => {
    entries.forEach((entry) => {
      if (entry.entryType === "measure") {
        this.performanceMetrics.renderTime = entry.duration;
      } else if (entry.entryType === "navigation") {
        const navEntry = entry as PerformanceNavigationTiming;
        this.performanceMetrics.interactionLatency =
          navEntry.loadEventEnd - navEntry.loadEventStart;
      }
    });

    this.performanceMetrics.lastUpdated = Date.now();

    // Track memory usage if available
    if ("memory" in performance) {
      this.performanceMetrics.memoryUsage = (
        performance as any
      ).memory.usedJSHeapSize;
    }
  };

  measureOperation = async <T>(
    name: string,
    operation: () => Promise<T>
  ): Promise<T> => {
    const startTime = performance.now();

    try {
      const result = await operation();
      const endTime = performance.now();

      performance.measure(name, { start: startTime, end: endTime });

      this.trackInteraction("performance-measure", name, {
        duration: endTime - startTime,
        success: true,
      });

      return result;
    } catch (error) {
      const endTime = performance.now();

      this.trackInteraction("performance-measure", name, {
        duration: endTime - startTime,
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  };

  // ========================================
  // USER INTERACTION TRACKING
  // ========================================

  trackInteraction = (
    action: string,
    target: string,
    metadata?: Record<string, any>
  ) => {
    const interaction: UserInteraction = {
      timestamp: Date.now(),
      action,
      target,
      metadata,
    };

    this.interactionBuffer.push(interaction);

    // Process buffer when it gets large
    if (this.interactionBuffer.length >= 50) {
      this.processInteractionBuffer();
    }
  };

  private processInteractionBuffer = () => {
    // Move interactions from buffer to main array
    this.interactions.push(...this.interactionBuffer);
    this.interactionBuffer = [];

    // Trim to max size
    if (this.interactions.length > this.maxInteractions) {
      this.interactions = this.interactions.slice(-this.maxInteractions);
    }
  };

  getInteractionStats = () => {
    const now = Date.now();
    const lastHour = now - 60 * 60 * 1000;
    const lastDay = now - 24 * 60 * 60 * 1000;

    const hourlyInteractions = this.interactions.filter(
      (i) => i.timestamp > lastHour
    );
    const dailyInteractions = this.interactions.filter(
      (i) => i.timestamp > lastDay
    );

    return {
      total: this.interactions.length,
      lastHour: hourlyInteractions.length,
      lastDay: dailyInteractions.length,
      topActions: this.getTopActions(dailyInteractions),
    };
  };

  private getTopActions = (interactions: UserInteraction[]) => {
    const actionCounts = interactions.reduce((acc, interaction) => {
      acc[interaction.action] = (acc[interaction.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(actionCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([action, count]) => ({ action, count }));
  };

  // ========================================
  // ENHANCED WORKER MANAGEMENT
  // ========================================

  spinUpWorker = () => {
    if (this.workerState === "connecting" || this.workerState === "connected") {
      return;
    }

    this.workerState = "connecting";
    const createdWorker = this.createWorker();
    if (createdWorker) {
      this.postMessageToWorker({
        accessToken: this.rootStore.authStore?.token,
      });
    }
  };

  postMessageToWorker = (message: unknown) => {
    console.log("Posting message to worker:", message);
    if (this.worker) {
      if (this.worker instanceof SharedWorker) {
        console.log("Posting message to shared worker");
        this.worker.port.postMessage(message);
      } else {
        console.log("Posting message to regular worker");
        this.worker.postMessage(message);
      }
    } else {
      console.error("No worker to post message to");
    }
  };

  updateUserChannels = (routeChannels: string[]) => {
    if (this.worker) {
      const authStore = this.rootStore.authStore;
      if (!authStore) {
        console.error("No authStore to get userId from");
        return;
      }
      const { appName, appVersion } = this.rootStore;
      const { userId, deviceId } = authStore;
      this.postMessageToWorker({
        channelRoutes: routeChannels,
        userId,
        appName,
        appVersion,
        deviceId,
      });
    } else {
      console.error("No worker to post message to");
    }
  };

  createWorker = () => {
    console.log("Spinning up worker");
    try {
      const isAndroidChrome =
        /Android/.test(navigator.userAgent) &&
        /Chrome/.test(navigator.userAgent);
      console.log("isAndroidChrome:", isAndroidChrome);
      const isAndroidWebView =
        /Android/.test(navigator.userAgent) &&
        !/Chrome/.test(navigator.userAgent) &&
        /wv/.test(navigator.userAgent);
      console.log("isAndroidWebView:", isAndroidWebView);

      const worker =
        isAndroidChrome || isAndroidWebView
          ? new Worker(
              new URL("./channelWorker.ts?worker&inline", import.meta.url),
              {
                type: "module",
              }
            )
          : new SharedWorker(
              new URL(
                "./sharedChannelWorker.ts?worker&inline",
                import.meta.url
              ),
              {
                type: "module",
              }
            );
      if (worker instanceof SharedWorker) {
        console.log("Created SharedWorker");
      } else {
        console.log("Created Worker");
      }
      this.setWorker(worker);
      return worker;
    } catch (error) {
      console.error("Error creating worker:", error);
      this.workerState = "error";
      this.workerErrorCount++;
      this.scheduleWorkerRetry();
    }
  };

  private scheduleWorkerRetry = () => {
    if (this.workerErrorCount < this.maxWorkerRetries) {
      const retryDelay = Math.min(
        1000 * Math.pow(2, this.workerErrorCount),
        30000
      ); // Exponential backoff

      this.workerRetryTimeout = setTimeout(() => {
        console.log(
          `Retrying worker creation (attempt ${this.workerErrorCount + 1})`
        );
        this.spinUpWorker();
      }, retryDelay);
    } else {
      console.error("Max worker retry attempts reached");
      this.workerState = "error";
    }
  };

  setWorker = (worker: Worker | SharedWorker) => {
    this.worker = worker;
    this.workerState = "connected";
    this.workerErrorCount = 0; // Reset error count on successful connection

    if (worker instanceof SharedWorker) {
      console.log("Starting SharedWorker");
      worker.port.start();

      // Heartbeat to keep connection alive
      setInterval(() => {
        if (this.workerState === "connected") {
          this.postMessageToWorker({
            ping: "ping",
          });
        }
      }, 10000);
    }
    this.addWorkerListeners(worker);
  };

  addWorkerListeners = (worker: Worker | SharedWorker) => {
    if (worker instanceof SharedWorker) {
      worker.port.onmessage = (e) => {
        console.log("Main received a message back from shared worker");
        console.log("Message:", e);
        const { data } = e;
        const { service, type } = data;
        const messageData = data.data;
        console.log("messageData", messageData);
        console.log("service", service);
        console.log("type", type);

        const currentServiceStore = `${utils.uriToCamel(service)}Store`;
        if (
          this.rootStore[currentServiceStore] &&
          (this.rootStore[currentServiceStore] as ServiceStore).isServiceStore
        ) {
          console.log("currentServiceStore", currentServiceStore);
          if (type === "created" || type === "patched") {
            (
              this.rootStore[currentServiceStore] as ServiceStore
            ).addOrUpdateEvent(messageData);
          }
          if (type === "removed") {
            (this.rootStore[currentServiceStore] as ServiceStore).removeEvent(
              messageData.id
            );
          }
        }
      };
    } else {
      worker.onmessage = (e) => {
        console.log("Main received a message back from worker");
        console.log("Message:", e);
        const { data } = e;
        const { service, type } = data;
        const messageData = data.data;
        console.log("messageData", messageData);
        console.log("service", service);
        console.log("type", type);

        const currentServiceStore = `${utils.uriToCamel(service)}Store`;
        if (
          this.rootStore[currentServiceStore] &&
          (this.rootStore[currentServiceStore] as ServiceStore).isServiceStore
        ) {
          console.log("currentServiceStore", currentServiceStore);
          if (type === "created" || type === "patched") {
            (
              this.rootStore[currentServiceStore] as ServiceStore
            ).addOrUpdateEvent(messageData);
          }
          if (type === "removed") {
            (this.rootStore[currentServiceStore] as ServiceStore).removeEvent(
              messageData.id
            );
          }
        }
      };
      worker.onerror = (error) => {
        console.error("Worker error:", error);
        this.workerState = "error";
        this.workerErrorCount++;
        this.scheduleWorkerRetry();
      };
      worker.onmessageerror = (error) => {
        console.error("Message error:", error);
        this.workerState = "error";
      };
    }
  };

  // ========================================
  // COMPUTED GETTERS
  // ========================================

  get isLoading() {
    return this.uiState.globalLoading;
  }

  get hasOpenDialogs() {
    return this.uiState.openDialogs.size > 0;
  }

  get topDialog() {
    return this.uiState.modalStack[this.uiState.modalStack.length - 1] || null;
  }

  get hasNotifications() {
    return this.uiState.notifications.length > 0;
  }

  get unreadNotifications() {
    return this.uiState.notifications.filter((n) => !n.persistent);
  }

  get activeFilterCount() {
    return Object.keys(this.uiState.activeFilters).length;
  }

  get currentTheme() {
    return {
      isDark: this.darkMode,
      mode: this.themeSettings.mode,
      colorScheme: this.themeSettings.colorScheme,
      fontSize: this.themeSettings.fontSize,
      animation: this.themeSettings.animation,
    };
  }

  get isHighContrast() {
    return (
      this.themeSettings.mode === "high-contrast" ||
      this.accessibilitySettings.highContrast
    );
  }

  get hasReducedMotion() {
    return (
      this.themeSettings.animation === "reduced" ||
      this.accessibilitySettings.reducedMotion
    );
  }

  get isWorkerConnected() {
    return this.workerState === "connected";
  }

  get performanceScore() {
    const metrics = this.performanceMetrics;
    let score = 100;

    // Penalize for slow render times
    if (metrics.renderTime > 100) score -= 20;
    if (metrics.renderTime > 500) score -= 30;

    // Penalize for high interaction latency
    if (metrics.interactionLatency > 100) score -= 15;
    if (metrics.interactionLatency > 300) score -= 25;

    // Penalize for high memory usage (if available)
    if (metrics.memoryUsage > 50 * 1024 * 1024) score -= 10; // 50MB
    if (metrics.memoryUsage > 100 * 1024 * 1024) score -= 20; // 100MB

    return Math.max(0, score);
  }

  // ========================================
  // LEGACY SUPPORT
  // ========================================

  toggleDarkMode = () => {
    this.setThemeMode(this.darkMode ? "light" : "dark");
  };

  get currentUserLanguage() {
    return (
      this.localizationSettings.language ||
      this.rootStore.authStore?.user?.languagePreference
    );
  }

  // ========================================
  // DISPOSAL AND CLEANUP
  // ========================================

  dispose = () => {
    // Clear timeouts
    if (this.workerRetryTimeout) {
      clearTimeout(this.workerRetryTimeout);
    }

    // Terminate worker
    if (this.worker) {
      if (this.worker instanceof SharedWorker) {
        this.worker.port.close();
      } else {
        this.worker.terminate();
      }
      this.worker = null;
    }

    // Disconnect performance observer
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }

    // Disconnect resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Process any remaining interactions
    if (this.interactionBuffer.length > 0) {
      this.processInteractionBuffer();
    }

    // Clear event listeners (if we stored references)
    if (typeof window !== "undefined") {
      // Note: In a real implementation, we'd store references to these handlers
      // and remove them here to prevent memory leaks
    }

    console.log("UIStore disposed");
  };
}

export default UIStore;
